import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdExpandMore, MdSearch } from 'react-icons/md';
import { GetCountries, GetState, GetCity } from 'react-country-state-city';

const normalizeValue = (value) => String(value ?? '').trim().toLowerCase();

const SearchableDropdownSelect = ({
  value,
  options,
  placeholder,
  disabled,
  onChange,
  error,
  emptyMessage = 'No results found',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOption = useMemo(
    () => options.find((option) => String(option.value) === String(value)) || null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const term = String(searchText).trim().toLowerCase();
    if (!term) {
      return options;
    }
    return options.filter((option) => String(option.label || '').toLowerCase().includes(term));
  }, [options, searchText]);

  useEffect(() => {
    if (!isOpen) {
      setSearchText('');
      return;
    }

    const timer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const displayValue = selectedOption?.label || (value ? String(value) : '');

  const handleSelect = (selectedValue) => {
    const isAlreadySelected = String(selectedValue) === String(value);
    onChange?.(isAlreadySelected ? '' : selectedValue);
    setSearchText('');
    setIsOpen(false);
  };

  return (
    <div className="position-relative" ref={wrapperRef}>
      <button
        type="button"
        className={`form-control rounded-3 shadow-sm text-start d-flex align-items-center justify-content-between w-100 ${
          error ? 'is-invalid' : ''
        }`}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        disabled={disabled}
        style={{ height: 37, lineHeight: 1.3 }}
      >
        <span
          className={`d-block w-100 text-truncate ${selectedOption || value ? 'text-dark' : 'text-secondary'}`}
        >
          {displayValue || placeholder}
        </span>
        <MdExpandMore size={20} />
      </button>

      <div
        className={`dropdown-menu w-100 mt-1 p-2 shadow overflow-y-auto overflow-x-hidden ${isOpen ? 'show' : ''}`}
        style={{ maxHeight: 280, boxSizing: 'border-box' }}
      >
        <div className="position-relative mb-2">
          <span
            className="position-absolute top-50 translate-middle-y text-secondary"
            style={{ left: 10, lineHeight: 0 }}
          >
            <MdSearch size={16} />
          </span>
          <input
            ref={searchInputRef}
            type="text"
            className="form-control form-control-sm"
            placeholder={`Search ${placeholder?.toLowerCase() || 'options'}...`}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onClick={(event) => event.stopPropagation()}
            disabled={disabled}
            style={{ paddingLeft: 30, color: 'var(--hc-body-text)' }}
          />
        </div>

        {filteredOptions.length > 0 ? (
          filteredOptions.map((item) => {
            const isSelected = String(item.value) === String(value);
            return (
              <button
                key={item.key}
                type="button"
                className={`dropdown-item rounded-2 d-flex align-items-center justify-content-between ${
                  isSelected ? 'hc-dropdown-item-selected' : ''
                }`}
                onClick={() => handleSelect(item.value)}
              >
                <span>{item.label}</span>
                {isSelected ? <MdExpandMore size={16} style={{ transform: 'rotate(180deg)' }} /> : null}
              </button>
            );
          })
        ) : (
          <div className="px-2 py-2 text-secondary small">{emptyMessage}</div>
        )}
      </div>
    </div>
  );
};

const AddressSelectorFields = ({
  title,
  fieldPrefix,
  values,
  errors,
  touched,
  onFieldChange,
  disabled = false,
  renderAsterisk,
}) => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const countryName = values?.country ?? '';
  const stateName = values?.state ?? '';
  const cityName = values?.city ?? '';
  

  useEffect(() => {
    let mounted = true;
    GetCountries()
      .then((list) => {
        if (!mounted) return;
        setCountries(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (mounted) setCountries([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!countryName || !countries.length) {
      setSelectedCountry(null);
      return;
    }

    const match = countries.find(
      (country) =>
        normalizeValue(country.name) === normalizeValue(countryName) ||
        normalizeValue(country.iso2) === normalizeValue(countryName) ||
        normalizeValue(country.iso3) === normalizeValue(countryName)
    );

    setSelectedCountry(match ?? null);
  }, [countryName, countries]);

  useEffect(() => {
    if (!selectedCountry?.id) {
      setStates([]);
      setSelectedState(null);
      return;
    }

    let mounted = true;
    GetState(selectedCountry.id)
      .then((list) => {
        if (!mounted) return;
        const stateList = Array.isArray(list) ? list : [];
        setStates(stateList);

        const match = stateList.find(
          (state) =>
            normalizeValue(state.name) === normalizeValue(stateName) ||
            normalizeValue(state.state_code) === normalizeValue(stateName)
        );

        setSelectedState(match ?? null);
      })
      .catch(() => {
        if (mounted) {
          setStates([]);
          setSelectedState(null);
        }
      });

    return () => {
      mounted = false;
    };
  }, [selectedCountry, stateName]);

  useEffect(() => {
    if (!selectedCountry?.id || !selectedState?.id) {
      setCities([]);
      setSelectedCity(null);
      return;
    }

    let mounted = true;
    GetCity(selectedCountry.id, selectedState.id)
      .then((list) => {
        if (!mounted) return;
        const cityList = Array.isArray(list) ? list : [];
        setCities(cityList);

        const match = cityList.find((city) => normalizeValue(city.name) === normalizeValue(cityName));
        setSelectedCity(match ?? null);
      })
      .catch(() => {
        if (mounted) {
          setCities([]);
          setSelectedCity(null);
        }
      });

    return () => {
      mounted = false;
    };
  }, [selectedCountry, selectedState, cityName]);

  const fieldName = (field) => `${fieldPrefix}.${field}`;
  const zipField = Object.prototype.hasOwnProperty.call(values ?? {}, 'zip') ? 'zip' : 'zipcode';
  const getError = (field) => {
    const baseError = touched?.[field] && errors?.[field] ? errors[field] : '';
    if (baseError) return baseError;

    if (field === 'zipcode') {
      return touched?.zip && errors?.zip ? errors.zip : '';
    }

    if (field === 'zip') {
      return touched?.zipcode && errors?.zipcode ? errors.zipcode : '';
    }

    return '';
  };

  return (
    <>
      <div className="mb-2">
        <input
          name={fieldName('street')}
          placeholder="Address Line 1"
          disabled={disabled}
          className={`form-control rounded-3 shadow-sm ${getError('street') ? 'is-invalid' : ''}`}
          style={{ color: 'var(--hc-body-text)' }}
          value={values?.street ?? ''}
          onChange={(e) => onFieldChange(fieldName('street'), e.target.value)}
        />
        <div className="invalid-feedback">{getError('street')}</div>
      </div>

      <div className="mb-2">
        <input
          name={fieldName('street2')}
          placeholder="Address Line 2"
          disabled={disabled}
          className="form-control rounded-3 shadow-sm"
          style={{ color: 'var(--hc-body-text)' }}
          value={values?.street2 ?? ''}
          onChange={(e) => onFieldChange(fieldName('street2'), e.target.value)}
        />
      </div>

      <div className="row g-2 mb-2">
        <div className="col-12 col-sm">
          <SearchableDropdownSelect
            value={countryName}
            placeholder="Select Country"
            disabled={disabled}
            error={getError('country')}
            options={countries.map((country) => ({
              key: country.id ?? country.iso2 ?? country.iso3,
              label: country.name,
              value: country.name,
            }))}
            onChange={(newCountry) => {
              const selected = countries.find((country) => country.name === newCountry);
              const newCountryName = selected?.name ?? newCountry ?? '';
              onFieldChange(fieldName('country'), newCountryName);
              onFieldChange(fieldName('state'), '');
              onFieldChange(fieldName('city'), '');
              setSelectedCountry(selected ?? null);
              setSelectedState(null);
              setSelectedCity(null);
              setCities([]);
            }}
          />
          <div className="invalid-feedback">{getError('country')}</div>
        </div>

        <div className="col-12 col-sm">
          <SearchableDropdownSelect
            value={stateName}
            placeholder="Select State"
            disabled={disabled || !selectedCountry?.id}
            error={getError('state')}
            options={states.map((state) => ({
              key: state.id ?? state.state_code,
              label: state.name,
              value: state.name,
            }))}
            onChange={(newState) => {
              const selected = states.find((state) => state.name === newState);
              const newStateName = selected?.name ?? newState ?? '';
              onFieldChange(fieldName('state'), newStateName);
              onFieldChange(fieldName('city'), '');
              setSelectedState(selected ?? null);
              setSelectedCity(null);
            }}
          />
          <div className="invalid-feedback">{getError('state')}</div>
        </div>
      </div>

      <div className="row g-2">
        <div className="col-12 col-sm">
          <SearchableDropdownSelect
            value={cityName}
            placeholder="Select City"
            disabled={disabled || !selectedState?.id}
            error={getError('city')}
            options={cities.map((city) => ({
              key: city.id ?? city.name,
              label: city.name,
              value: city.name,
            }))}
            onChange={(newCity) => {
              const selected = cities.find((city) => city.name === newCity);
              const newCityName = selected?.name ?? newCity ?? '';
              onFieldChange(fieldName('city'), newCityName);
              setSelectedCity(selected ?? null);
            }}
          />
          <div className="invalid-feedback">{getError('city')}</div>
        </div>

        <div className="col-12 col-sm">
          <input
            name={fieldName(zipField)}
            placeholder="ZIP Code"
            disabled={disabled}
            className={`form-control rounded-3 shadow-sm ${getError(zipField) ? 'is-invalid' : ''}`}
            style={{ color: 'var(--hc-body-text)' }}
            value={values?.[zipField] ?? ''}
            onChange={(e) => onFieldChange(fieldName(zipField), e.target.value)}
          />
          <div className="invalid-feedback">{getError(zipField)}</div>
        </div>
      </div>
    </>
  );
};

export default AddressSelectorFields;
